"use client";

import React, { useRef } from "react";
import { Settings2 } from "lucide-react";
import type { Editor } from "@tiptap/react";
import type { UnifiedResponsiveStyles } from "../modals/ResponsiveStylesModal";

/** Captured when opening Text Styles so apply still uses the same range after focus moves to the modal. */
export type TextStylesSelectionCapture = { from: number; to: number };

interface FontControlsToolbarProps {
  editor: Editor | null;
  onShowResponsiveStylesModal: (styles: UnifiedResponsiveStyles, selection: TextStylesSelectionCapture) => void;
}

export const FontControlsToolbar: React.FC<FontControlsToolbarProps> = ({
  editor,
  onShowResponsiveStylesModal,
}) => {
  /** Mousedown on the toolbar runs before click; without this, the editor blurs and the range collapses before click. */
  const textStylesSelectionSnapshotRef = useRef<TextStylesSelectionCapture | null>(null);

  const extractUnifiedResponsiveStyles = (range?: TextStylesSelectionCapture): UnifiedResponsiveStyles => {
    const defaultStyles: UnifiedResponsiveStyles = {
      xs: { fontSize: "", fontWeight: "", textAlign: "" },
      sm: { fontSize: "", fontWeight: "", textAlign: "" },
      md: { fontSize: "", fontWeight: "", textAlign: "" },
      lg: { fontSize: "", fontWeight: "", textAlign: "" },
      xl: { fontSize: "", fontWeight: "", textAlign: "" },
    };

    if (!editor) return defaultStyles;

    const from = range ? Math.min(range.from, range.to) : editor.state.selection.from;
    const to = range ? Math.max(range.from, range.to) : editor.state.selection.to;
    let responsiveClass = "";
    let blockClassAttr: string | null = null; // block's class string (for legacy extraction when collapsed)
    const prefixes = ["responsive-style-", "responsive-font-", "responsive-weight-", "responsive-align-"];

    const collectClassFromNode = (node: { type: { name: string }; attrs: { class?: string } }) => {
      if (
        (node.type.name === "paragraph" || node.type.name.startsWith("heading")) &&
        node.attrs.class
      ) {
        blockClassAttr = node.attrs.class;
        const classes = node.attrs.class.split(" ");
        for (const prefix of prefixes) {
          const matchingClasses = classes.filter((cls: string) => cls.startsWith(prefix));
          if (matchingClasses.length > 0) {
            responsiveClass = matchingClasses[matchingClasses.length - 1];
            return true;
          }
        }
      }
      return false;
    };

    const collectClassFromResponsiveSpan = ($pos: typeof editor.state.selection.$from) => {
      for (let d = $pos.depth; d > 0; d--) {
        const node = $pos.node(d);
        if (node.type.name !== "responsiveStyleSpan" || !node.attrs.class) continue;
        blockClassAttr = String(node.attrs.class);
        const classes = blockClassAttr.split(" ");
        for (const prefix of prefixes) {
          const matchingClasses = classes.filter((cls: string) => cls.startsWith(prefix));
          if (matchingClasses.length > 0) {
            responsiveClass = matchingClasses[matchingClasses.length - 1];
            return true;
          }
        }
      }
      return false;
    };

    // Prefer inline responsive span (partial styling); otherwise paragraph/heading block class
    if (from === to) {
      const $from = editor.state.doc.resolve(from);
      if (!collectClassFromResponsiveSpan($from)) {
        for (let d = $from.depth; d >= 0; d--) {
          const node = $from.node(d);
          if (collectClassFromNode(node)) break;
        }
      }
    } else {
      const $from = editor.state.doc.resolve(from);
      const $to = editor.state.doc.resolve(to);
      if (!collectClassFromResponsiveSpan($from) && !collectClassFromResponsiveSpan($to)) {
        editor.state.doc.nodesBetween(from, to, (node) => {
          collectClassFromNode(node);
        });
      }
    }

    if (!responsiveClass) return defaultStyles;

    const html = editor.getHTML();
    const styleMatch = html.match(/<style>([\s\S]*?)<\/style>/);

    if (!styleMatch) return defaultStyles;

    const cssContent = styleMatch[1];
    const extractedStyles = JSON.parse(JSON.stringify(defaultStyles));

    // Helper to extract a property value from CSS
    const extractProperty = (className: string, property: string, regex: RegExp, breakpoint?: string): string => {
      let pattern: RegExp;
      if (breakpoint) {
        pattern = new RegExp(
          `@media\\s*\\(min-width:\\s*${breakpoint}\\)[^}]*\\.${className}\\s*{[^}]*${property}:\\s*${regex.source}`,
          "i"
        );
      } else {
        pattern = new RegExp(`\\.${className}\\s*{[^}]*${property}:\\s*${regex.source}`, "i");
      }
      const match = cssContent.match(pattern);
      return match ? match[1] : "";
    };

    const breakpoints = [
      { key: "sm" as const, width: "1080px" },
      { key: "md" as const, width: "1230px" },
      { key: "lg" as const, width: "1430px" },
      { key: "xl" as const, width: "1750px" },
    ];

    // If it's a unified class, extract all properties
    if (responsiveClass.startsWith("responsive-style-")) {
      // Base values (xs)
      extractedStyles.xs.fontSize = extractProperty(responsiveClass, "font-size", /(\d+)px/);
      extractedStyles.xs.fontWeight = extractProperty(responsiveClass, "font-weight", /(\d+)/);
      extractedStyles.xs.textAlign = extractProperty(responsiveClass, "text-align", /(left|center|right|justify)/);

      // Breakpoint values
      breakpoints.forEach(({ key, width }) => {
        extractedStyles[key].fontSize = extractProperty(responsiveClass, "font-size", /(\d+)px/, width);
        extractedStyles[key].fontWeight = extractProperty(responsiveClass, "font-weight", /(\d+)/, width);
        extractedStyles[key].textAlign = extractProperty(responsiveClass, "text-align", /(left|center|right|justify)/, width);
      });
    } else {
      // Legacy: Extract from separate classes (use block's class from extraction, or node at from when range)
      const legacyPrefixes = {
        fontSize: "responsive-font-",
        fontWeight: "responsive-weight-",
        textAlign: "responsive-align-",
      };

      const classStr = blockClassAttr ?? editor.state.doc.nodeAt(from)?.attrs?.class;
      if (classStr) {
        const classes = classStr.split(" ");
        
        Object.entries(legacyPrefixes).forEach(([prop, prefix]) => {
          const legacyClass = classes.find((cls: string) => cls.startsWith(prefix));
          if (legacyClass) {
            const regex = prop === "fontSize" ? /(\d+)px/ : prop === "fontWeight" ? /(\d+)/ : /(left|center|right|justify)/;
            const cssProp = prop === "fontSize" ? "font-size" : prop === "fontWeight" ? "font-weight" : "text-align";
            
            // Base value
            (extractedStyles.xs as any)[prop] = extractProperty(legacyClass, cssProp, regex);
            
            // Breakpoint values
            breakpoints.forEach(({ key, width }) => {
              (extractedStyles[key] as any)[prop] = extractProperty(legacyClass, cssProp, regex, width);
            });
          }
        });
      }
    }

    return extractedStyles;
  };

  const handleTextStylesPointerDown = (e: React.PointerEvent<HTMLButtonElement>) => {
    if (!editor) return;
    if (e.pointerType === "mouse" && e.button !== 0) return;
    const { from, to } = editor.state.selection;
    textStylesSelectionSnapshotRef.current = { from, to };
    // Keeps focus in the editor so the selection is not cleared before click / modal open.
    e.preventDefault();
  };

  const handleTextStylesClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!editor) return;
    const snap = textStylesSelectionSnapshotRef.current;
    textStylesSelectionSnapshotRef.current = null;
    const range: TextStylesSelectionCapture = snap ?? {
      from: editor.state.selection.from,
      to: editor.state.selection.to,
    };
    const styles = extractUnifiedResponsiveStyles(range);
    onShowResponsiveStylesModal(styles, range);
  };

  const buttonClass =
    "px-3 py-1.5 border border-gray-300 rounded-md text-sm font-medium bg-white hover:bg-gray-50 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary-main/20 focus:border-primary-main flex items-center gap-2";

  return (
    <div className="flex gap-2 p-2 border-b bg-gray-50 items-center">
      <button
        type="button"
        onPointerDown={handleTextStylesPointerDown}
        onClick={handleTextStylesClick}
        className={buttonClass}
      >
        <Settings2 className="w-4 h-4" />
        <span>Text Styles</span>
      </button>
    </div>
  );
};
